import { SupabaseClient } from "@supabase/supabase-js";

/** Normalizes rep_emails from JSONB — handles both array and double-serialized string. */
function parseRepEmails(raw: unknown): { email: string }[] {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
    } catch {
      return [];
    }
  }
  return [];
}

/**
 * Looks up enterprise preferred rep emails for a venue by checking both
 * enterprise_preferred_venues (direct match) and enterprise_preferred_chains (chain match).
 * Returns a deduplicated, lowercased array of email strings.
 *
 * @param supabase - Supabase client. MUST be service-role: enterprise_preferred_venues
 *   and enterprise_preferred_chains grant SELECT to service_role only, so an
 *   authenticated client gets permission-denied and this returns [] (no reps).
 * @param enterpriseOrgId - The enterprise_organizations.id (callers must resolve from org_id first).
 * @param venueStaticId - The venue static ID to match against preferred venues.
 * @param chainId - Optional chain ID to match against preferred chains.
 * @param brandId - Optional brand ID for brand-specific chain matching.
 * @returns Deduplicated, lowercased array of rep email strings.
 */
export async function getEnterprisePreferredRepEmails(
  supabase: SupabaseClient,
  enterpriseOrgId: number,
  venueStaticId: number,
  chainId?: number | null,
  brandId?: number | null
): Promise<string[]> {
  if (!enterpriseOrgId || !venueStaticId) return [];

  const venueQuery = supabase
    .schema("nowadays")
    .from("enterprise_preferred_venues")
    .select("rep_emails")
    .eq("organization_id", enterpriseOrgId)
    .eq("venue_static_id", venueStaticId)
    .maybeSingle();

  const chainQuery = chainId
    ? supabase
        .schema("nowadays")
        .from("enterprise_preferred_chains")
        .select("rep_emails, brand_id")
        .eq("organization_id", enterpriseOrgId)
        .eq("chain_id", chainId)
    : null;

  const [{ data: venueData, error: venueError }, chainResult] = await Promise.all([
    venueQuery,
    chainQuery ?? Promise.resolve({ data: null, error: null }),
  ]);

  // Surface read failures instead of swallowing them. These tables grant SELECT
  // to service_role only, so an authenticated client returns a permission error
  // (Postgres 42501) that is otherwise indistinguishable from "no reps
  // configured" — logging it turns a silent CC-nobody data loss into a signal
  // the wrong client was passed (the bug ENG-352 fixed).
  if (venueError || chainResult.error) {
    const describeError = (e: { code?: string; message?: string } | null) =>
      e ? { code: e.code, message: e.message } : null;
    console.error("[enterprise-reps] failed to read preferred rep emails (needs service-role client):", {
      enterpriseOrgId,
      venueError: describeError(venueError),
      chainError: describeError(chainResult.error),
    });
  }

  const emails = new Set<string>();
  for (const rep of parseRepEmails(venueData?.rep_emails)) {
    if (rep.email) emails.add(rep.email.toLowerCase());
  }

  if (chainResult.data) {
    for (const row of chainResult.data) {
      if (row.brand_id != null && row.brand_id !== brandId) continue;
      for (const rep of parseRepEmails(row.rep_emails)) {
        if (rep.email) emails.add(rep.email.toLowerCase());
      }
    }
  }

  return Array.from(emails);
}
